import { useQuery, useMutation } from '@tanstack/react-query'
import Card from 'react-bootstrap/Card'
import { fetchProfileOccupations } from './data'
import type { Occupation } from './data'
import { RandomColorBox } from '@/component/RandomColorBox'
import { BriefcaseIcon } from 'lucide-react'
import { SquarePen } from 'lucide-react'
import { useSelectable } from '@/hook'
import { syncProgress } from '../helper'
import { useEffect, useState } from 'react'
import { queryClient } from '@/util/queryClient'
import { syncOccupations } from '../helper'
import { fetchOccupations, type OccupationCategory } from '@/util/ref'
import { ProgressFlags } from '@/util/progress'
import Container from 'react-bootstrap/esm/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import type { JSX } from 'react'
import clsx from 'clsx'
import { cardStyle, active, cursorPointer } from './style.css'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

export default function DesiredJobPanel() {
  const { data: desiredOccupations } = useQuery<Occupation[]>({
    queryKey: ['profile', 'occupation'],
    queryFn: fetchProfileOccupations,
    staleTime: 1000 * 60 * 5,
  })
  const { data: occupations } = useQuery<Occupation[]>({
    queryKey: ['profile', 'occupation'],
    queryFn: fetchProfileOccupations,
    staleTime: 1000 * 60 * 5,
  })
  const { data: occupationCategories } = useQuery<OccupationCategory[]>({
    queryKey: ['occupation'],
    queryFn: fetchOccupations,
    staleTime: 1000 * 60 * 5,
  })
  const [selectedOccupationIds, toggle, reset] = useSelectable<number>([])
  const occupationsMutation = useMutation({
    mutationFn: (occupationIds: number[]) => syncOccupations(occupationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', 'occupation'] })
      syncProgress(ProgressFlags.JOB_PREFS_SELECTED)
    },
    onError: (err) => console.log(err),
  })

  useEffect(() => {
    reset(occupations ? occupations.map(({ id }) => id) : [])
  }, [reset, occupations])

  const [editMode, setEditMode] = useState<boolean>(false)

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <div>
              <BriefcaseIcon className="me-1" />
              志望職種
            </div>
            {!editMode && <SquarePen onClick={() => setEditMode(true)} className={cursorPointer} />}
          </Card.Title>
          {editMode ? (
            <>
              <p className="text-muted small">
                関心のある職種を3つ以上選択してください。あなたの志望に合った企業をおすすめします。
              </p>
              <Container>
                {occupationCategories?.reduce<JSX.Element[]>((acc, cur, i, arr) => {
                  const next = arr[i + 1]
                  if (i % 2 === 0) {
                    acc.push(
                      <Row className="mb-2" key={`row-${cur.id}`}>
                        <Col key={cur.id}>
                          <Card
                            className={clsx(
                              cardStyle,
                              selectedOccupationIds.includes(cur.reprId) && active
                            )}
                            onClick={() => toggle(cur.reprId)}
                          >
                            <Card.Body>
                              <b>
                                {cur.emoji} {cur.name}
                              </b>
                              <div className="text-muted">
                                {cur.description || cur.occupations.map((i) => i.name).join(', ')}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                        {next && (
                          <Col key={next.id}>
                            <Card
                              className={clsx(
                                cardStyle,
                                selectedOccupationIds.includes(next.reprId) && active
                              )}
                              onClick={() => toggle(next.reprId)}
                            >
                              <Card.Body>
                                <b>
                                  {next.emoji} {next.name}
                                </b>
                                <div className="text-muted">
                                  {next.description ||
                                    next.occupations.map((i) => i.name).join(', ')}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        )}
                      </Row>
                    )
                  }
                  return acc
                }, [])}
                <Stack direction="horizontal" gap={3} className="justify-content-end">
                  <Button variant="outline-secondary" onClick={() => setEditMode(false)}>
                    キャンセル
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={selectedOccupationIds.length < 3}
                    onClick={() => {
                      occupationsMutation.mutate(selectedOccupationIds)
                      setEditMode(false)
                    }}
                  >
                    保存
                  </Button>
                </Stack>
              </Container>
            </>
          ) : desiredOccupations ? (
            desiredOccupations.map(({ id, name, emoji }, i) => (
              <RandomColorBox key={id} index={i}>
                {emoji} {name}
              </RandomColorBox>
            ))
          ) : (
            <Card.Subtitle className="text-muted">志望職種が設定されていません</Card.Subtitle>
          )}
        </Card.Body>
      </Card>
    </>
  )
}
